<template>
	<Modals
		:title="'History Log Form'"
		:display="true"
		@show="close"
		:parentTag="'form'"
		:novalidate="'novalidate'"
		:fullMobile="true"
	>
		<template #modal-content>
			<p>{{ actionDetails }}</p>
			<Table
				:sourceObject="formLogData"
				:tableMobilev1="true"
				:paginationFeature="true"
				@change="updatePagination"
			>
				<template #body-ChangedPropertyName="{ body }">
					<span v-html="formatPropertyName(body)" />
				</template>
				<template #body-OriginalValue="{ body }">
					<span
						v-html="formatOriginalValue(body)"
						data-test="originalValue"
					/>
				</template>
				<template #body-NewValue="{ body }">
					<span v-html="formatNewValue(body)" data-test="newValue" />
				</template>
			</Table>
		</template>
		<template #modal-footer>
			<Button
				id="linkButton"
				className="btn--primary"
				labelText="Close"
				:btnClick="close"
			/>
		</template>
	</Modals>
</template>

<script>
import Modals from '@/components/shells/modal/modals';
import Button from '@/components/forms/button/button';
import Table from '@/components/shells/table/table';
import DateTimeMixin from '@/mixins/datetime-mixin';
import HistoryHelpers from '@/mixins/history-helpers';
import { mapState } from 'vuex';

export default {
	name: 'LogHistoryDetailModal',
	components: {
		Modals,
		Button,
		Table
	},
	model: {
		prop: 'display',
		event: 'show'
	},
	props: {
		title: {
			type: String,
			default: ''
		},
		parentTag: {
			type: String,
			default: 'div'
		},
		display: {
			type: Boolean,
			default: false
		},
		logs: {
			type: Object,
			default: () => {}
		}
	},
	mixins: [HistoryHelpers, DateTimeMixin],
	data() {
		return {
			showModal: this.display,
			formLogData: {
				headers: [
					{
						key: 'ChangedPropertyName',
						label: 'Changed Property',
						sorteable: false
					},
					{
						key: 'ImpactType',
						label: 'Field Type',
						sorteable: false
					},
					{
						key: 'OriginalValue',
						label: 'Original Value',
						sorteable: false
					},
					{
						key: 'NewValue',
						label: 'New Value',
						sorteable: false
					},
					{
						key: 'Comment',
						label: 'Comment',
						sorteable: false
					}
				],
				displayHeader: true,
				source: {}
			},
			allFormatedItems: [],
			currentPageDate: [],
			headersNoImpactForm: [
				{
					key: 'ChangedPropertyName',
					label: 'Changed Property',
					sorteable: false
				},
				{
					key: 'OriginalValue',
					label: 'Original Value',
					sorteable: false
				},
				{
					key: 'NewValue',
					label: 'New Value',
					sorteable: false
				}
			]
		};
	},
	created() {
		if (!this.isImpactForm) {
			this.formLogData.headers = this.headersNoImpactForm;
		}

		this.formatChanges();

		this.updatePagination({ page: 1, pageSize: 5 });
	},
	computed: {
		...mapState('form', ['form']),
		isImpactForm() {
			const type = this.form.formPrintType;
			return type === 'I';
		}
	},
	methods: {
		close() {
			this.$emit('close', false);
		},
		formatChanges() {
			if (this.isImpactForm) {
				const changes = [...this.logs.fieldChanges.items];

				// remove duplicates
				for (let i = 0; i < changes.length; i++) {
					let changeI = changes[i];

					const attr = this.returnAttr(changeI.ChangedPropertyName);
					if (attr) {
						changeI[attr] = {
							OriginalValue: changeI.OriginalValue,
							NewValue: changeI.NewValue
						};
					}

					for (let j = i + 1; j < changes.length; j++) {
						const changeJ = changes[j];

						const idI = this.returnId(changeI.ChangedPropertyName);
						const idJ = this.returnId(changeJ.ChangedPropertyName);

						const attrI = this.returnAttr(
							changeI.ChangedPropertyName
						);
						const attrJ = this.returnAttr(
							changeJ.ChangedPropertyName
						);

						if (
							idI === idJ &&
							this.isRDC(attrI) &&
							this.isRDC(attrJ)
						) {
							changeI[attrI] = {
								OriginalValue: changeI.OriginalValue,
								NewValue: changeI.NewValue
							};

							changeI[attrJ] = {
								OriginalValue: changeJ.OriginalValue,
								NewValue: changeJ.NewValue
							};

							changes.splice(j, 1);
							j--;
						}
					}
				}

				// format ImpactInputFields
				for (let i = 0; i < changes.length; i++) {
					const change = changes[i];

					if (this.isInputFields(change)) {
						const changeId = parseInt(
							this.returnId(change.ChangedPropertyName).replace(
								/\D/g,
								''
							)
						);

						// find input field on data
						let inputFieldToFind;
						const items = this.logs.data.items;

						for (let i = 0; i < items.length; i++) {
							const item = items[i];

							const inputFields = item.ImpactInputFields;
							for (let j = 0; j < inputFields.length; j++) {
								const inputField = inputFields[j];

								if (changeId === inputField.Id) {
									inputFieldToFind = inputField;
									break;
								}
							}
							if (inputFieldToFind) {
								break;
							}
						}

						change.ImpactType = this.formatImpactType(
							inputFieldToFind.ImpactType
						);

						if (this.isInputFieldsWithRDC(change)) {
							// find the missing attr
							if (!change.Row || !change.Dot || !change.Col) {
								if (!change.Row) {
									change.Row = {
										OriginalValue: inputFieldToFind.Row,
										NewValue: inputFieldToFind.Row
									};
								}
								if (!change.Dot) {
									change.Dot = {
										OriginalValue: inputFieldToFind.Dot,
										NewValue: inputFieldToFind.Dot
									};
								}
								if (!change.Col) {
									change.Col = {
										OriginalValue: inputFieldToFind.Col,
										NewValue: inputFieldToFind.Col
									};
								}
							}
						} else {
							change.Row = {
								OriginalValue: inputFieldToFind.Row,
								NewValue: inputFieldToFind.Row
							};
							change.Dot = {
								OriginalValue: inputFieldToFind.Dot,
								NewValue: inputFieldToFind.Dot
							};
							change.Col = {
								OriginalValue: inputFieldToFind.Col,
								NewValue: inputFieldToFind.Col
							};
						}
					}
				}

				this.formLogData.source.items = changes;
			} else {
				this.formLogData.source.items = this.logs.fieldChanges.items;
			}
		},
		formatImpactType(text) {
			if (text === 'C') {
				return 'Code';
			} else if (text === 'S') {
				return 'Static';
			} else if (text === 'I') {
				return 'Client Input';
			}
			return '';
		},
		formatPropertyName(data) {
			let name = data.ChangedPropertyName;

			if (data.Action === 'Update') {
				if (name.indexOf('.') !== -1) {
					const matchWithAttr = name.match(/[^.]+$/);

					name = this.removeAttrPropName(name);

					if (matchWithAttr) {
						if (!this.isInputFields(data)) {
							return `
							<p><strong>${name}</strong></p>
							<span>${matchWithAttr[0]}</span>
							`;
						} else {
							return `
							<p><strong>${name}</strong></p>
							<p>R/D/C: ${data.Row.NewValue}/${data.Dot.NewValue}/${data.Col.NewValue}</p>
							<span>${matchWithAttr[0]}</span>
							`;
						}
					}
				}
			}

			if (!this.isInputFields(data)) {
				return `<strong> ${this.removeIdPropName(
					data.ChangedPropertyName
				)}</strong>`;
			}

			return `
				<p><strong>${name}</strong></p>
				<p>R/D/C: ${data.Row.NewValue}/${data.Dot.NewValue}/${data.Col.NewValue}</p>
				`;
		},
		isRDC(text) {
			return ['Row', 'Dot', 'Col'].includes(text);
		},
		isInputFieldsWithRDC(data) {
			return this.isRDC(this.returnAttr(data.ChangedPropertyName));
		},
		isInputFields(data) {
			return data.ChangedPropertyName.indexOf('ImpactInputFields') === 0;
		}
	}
};
</script>

<style lang="scss" scoped></style>
